export const DEFAULT_STORY_INTEGRITY_WEIGHTS = Object.freeze({
  a_E: 0.20,
  a_R: 0.30,
  a_I: 0.20,
  a_F: 0.15,
  a_C: 0.15,
});

export const STORY_INTEGRITY_PILLARS = [
  { key: 'c_entity', weightKey: 'a_E', label: 'Entity' },
  { key: 'c_reproducibility', weightKey: 'a_R', label: 'Reproducibility' },
  { key: 'c_impact', weightKey: 'a_I', label: 'Impact' },
  { key: 'c_freshness', weightKey: 'a_F', label: 'Freshness' },
  { key: 'c_confidence', weightKey: 'a_C', label: 'Confidence' },
];

function round4(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 10000) / 10000;
}

export function normalizeIntegrityWeights(weights) {
  const merged = {
    ...DEFAULT_STORY_INTEGRITY_WEIGHTS,
    ...(weights && typeof weights === 'object' ? weights : {}),
  };
  const total = Object.values(merged).reduce((acc, value) => {
    const n = Number(value);
    return acc + (Number.isFinite(n) && n >= 0 ? n : 0);
  }, 0);
  if (!(total > 0)) return { ...DEFAULT_STORY_INTEGRITY_WEIGHTS };
  return Object.keys(DEFAULT_STORY_INTEGRITY_WEIGHTS).reduce((acc, key) => {
    acc[key] = round4(Number(merged[key]) / total);
    return acc;
  }, {});
}

function toWeights(raw) {
  if (!raw) return { ...DEFAULT_STORY_INTEGRITY_WEIGHTS };
  if (typeof raw === 'object') return normalizeIntegrityWeights(raw);
  try {
    return normalizeIntegrityWeights(JSON.parse(String(raw)));
  } catch (err) {
    return { ...DEFAULT_STORY_INTEGRITY_WEIGHTS };
  }
}

function toScore(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(1, n));
}

export function getStoryIntegrityData(story) {
  if (!story || typeof story !== 'object') return null;
  const weights = toWeights(story.integrity_weights);
  const components = STORY_INTEGRITY_PILLARS.map((pillar) => {
    const value = toScore(story[pillar.key]);
    const weight = Number(weights[pillar.weightKey] || 0);
    if (value == null) return null;
    return {
      ...pillar,
      value,
      weight,
      contribution: round4(value * weight),
    };
  }).filter(Boolean);

  if (!components.length) return null;

  const composite = toScore(story.c_story);
  const tau = toScore(story.tau_s);
  return {
    family: String(story.story_type || '').toUpperCase() || 'UNKNOWN',
    weights,
    components,
    composite: composite == null ? round4(components.reduce((acc, item) => acc + item.contribution, 0)) : composite,
    tau: tau == null ? 0.5 : tau,
    admissible: Number(story.admissible) === 1,
  };
}
